import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import {
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
} from '@mui/icons-material';

const Footer = () => {
  const theme = useTheme();

  const footerSections = [
    {
      title: '服务项目',
      links: [
        { text: '院校申请', href: '/services' },
        { text: '签证指导', href: '/services' },
        { text: '语言培训', href: '/services' },
        { text: '住宿安排', href: '/services' },
        { text: '接机服务', href: '/services' },
      ],
    },
    {
      title: '热门院校',
      links: [
        { text: '马来亚大学', href: '/universities/university-of-malaya' },
        { text: '博特拉大学', href: '/universities/universiti-putra-malaysia' },
        { text: '国民大学', href: '/universities/the-national-university-of-malaysia' },
        { text: '理科大学', href: '/universities/university-of-science-malaysia' },
        { text: '工艺大学', href: '/universities/universiti-teknologi-mara' },
      ],
    },
    {
      title: '留学资讯',
      links: [
        { text: '申请指南', href: '/about' },
        { text: '专业解析', href: '/programs' },
        { text: '生活指南', href: '/about' },
        { text: '政策解读', href: '/about' },
        { text: '成功案例', href: '/success-cases' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.grey[900],
        color: theme.palette.common.white,
        pt: 6,
        pb: 3,
        mt: 8,
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon sx={{ mr: 1, fontSize: 32, color: theme.palette.primary.light }} />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  希航教育
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 3, color: theme.palette.grey[300] }}>
                专业的马来西亚留学服务机构，为您提供从院校选择到成功入学的全程服务。
                我们致力于让每一位学生都能找到最适合的教育机会。
              </Typography>
              
              {/* Contact Info */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon sx={{ mr: 1, fontSize: 18 }} />
                  <Typography variant="body2">0551-62888215</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon sx={{ mr: 1, fontSize: 18 }} />
                  <Typography variant="body2">+86 19956586698</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ mr: 1, fontSize: 18 }} />
                  <Typography variant="body2">xihangedu@qq.com</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationIcon sx={{ mr: 1, fontSize: 18 }} />
                  <Typography variant="body2">合肥市包河区万达广场7号写字楼1805室</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <Grid item xs={12} sm={6} md={2} key={index}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {section.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {section.links.map((link, linkIndex) => (
                  <Box component="li" key={linkIndex} sx={{ mb: 1 }}>
                    <Link
                      href={link.href}
                      sx={{
                        color: theme.palette.grey[300],
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        '&:hover': {
                          color: theme.palette.primary.light,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {link.text}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>
          ))}

          {/* Newsletter & Social */}
          <Grid item xs={12} md={2}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              关注我们
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: theme.palette.grey[300] }}>
              获取最新的留学资讯和申请指南
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { icon: <FacebookIcon />, color: '#1877f2' },
                { icon: <InstagramIcon />, color: '#E4405F' },
                { icon: <LinkedInIcon />, color: '#0077b5' },
                { icon: <TwitterIcon />, color: '#1da1f2' },
              ].map((social, index) => (
                <IconButton
                  key={index}
                  sx={{
                    color: theme.palette.grey[300],
                    '&:hover': {
                      color: social.color,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: theme.palette.grey[700] }} />

        {/* Bottom Footer */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.grey[400] }}>
            © 2025 希航教育. 保留所有权利。
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              href="/privacy"
              sx={{
                color: theme.palette.grey[400],
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: theme.palette.primary.light,
                },
              }}
            >
              隐私政策
            </Link>
            <Link
              href="/terms"
              sx={{
                color: theme.palette.grey[400],
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: theme.palette.primary.light,
                },
              }}
            >
              服务条款
            </Link>
            <Link
              href="/sitemap"
              sx={{
                color: theme.palette.grey[400],
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: theme.palette.primary.light,
                },
              }}
            >
              网站地图
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
